import { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Plus, Search, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

export type StageIdeaPickerStage = 'setup' | 'complication' | 'resolution';

type CandidateIdea = {
  id: number;
  title: string | null;
  seed_idea: string | null;
  arc_slug: string | null;
  arc_stage: string | null;
  status: string | null;
};

interface StageIdeaPickerModalProps {
  isOpen: boolean;
  arcSlug: string;
  arcLabel: string;
  stage: StageIdeaPickerStage;
  stageLabel: string;
  /** Thought ids already in this stage on this arc — shown but not selectable. */
  occupiedThoughtIds: Set<number>;
  onClose: () => void;
  onCreateNew: () => void;
  onAttach: (thoughtId: number) => Promise<void>;
}

export default function StageIdeaPickerModal({
  isOpen,
  arcSlug,
  arcLabel,
  stage,
  stageLabel,
  occupiedThoughtIds,
  onClose,
  onCreateNew,
  onAttach,
}: StageIdeaPickerModalProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachingId, setAttachingId] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<CandidateIdea[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setAttachingId(null);
      return;
    }
    let active = true;
    setLoading(true);
    void supabase
      .from('thoughts')
      .select('id, title, seed_idea, arc_slug, arc_stage, status')
      .neq('status', 'dead')
      .order('updated_at', { ascending: false })
      .limit(150)
      .then(({ data, error }) => {
        if (!active) return;
        setLoading(false);
        if (error) {
          toast.error(error.message);
          setCandidates([]);
          return;
        }
        setCandidates((data ?? []) as CandidateIdea[]);
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && attachingId == null) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, attachingId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return candidates.filter((c) => {
      if (occupiedThoughtIds.has(c.id)) return false;
      if (!q) return true;
      const hay = `${c.title ?? ''} ${c.seed_idea ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [candidates, occupiedThoughtIds, query]);

  if (!isOpen) return null;

  async function attach(id: number) {
    if (attachingId != null) return;
    setAttachingId(id);
    try {
      await onAttach(id);
      onClose();
    } catch {
      // parent toasts
    } finally {
      setAttachingId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stage-idea-picker-title"
      onClick={() => {
        if (attachingId == null) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-mono text-gray-400 dark:text-gray-500">
              {arcLabel} · {stageLabel}
            </div>
            <h3
              id="stage-idea-picker-title"
              className="text-base font-semibold text-gray-900 dark:text-white"
            >
              Add idea to stage
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={attachingId != null}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 border-b border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={onCreateNew}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4" />
            Create new idea
          </button>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center">
            or attach an existing idea to {stageLabel.toLowerCase()}
          </p>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ideas…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900"
            />
          </div>
        </div>

        <ul className="flex-1 overflow-y-auto px-2 pb-3 min-h-[180px]">
          {loading && (
            <li className="px-2 py-6 text-center text-sm text-gray-400">Loading ideas…</li>
          )}
          {!loading && filtered.length === 0 && (
            <li className="px-2 py-6 text-center text-sm text-gray-400">
              No matching ideas. Create a new one above.
            </li>
          )}
          {!loading &&
            filtered.map((c) => {
              const onThisArc = c.arc_slug === arcSlug;
              const elsewhere = c.arc_slug && c.arc_slug !== arcSlug;
              const stageNote = c.arc_stage
                ? c.arc_stage.charAt(0).toUpperCase() + c.arc_stage.slice(1)
                : 'Unstaged';
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={attachingId != null}
                    onClick={() => void attach(c.id)}
                    className="w-full text-left px-2.5 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/60 disabled:opacity-50 flex items-start gap-2"
                  >
                    <Lightbulb className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {c.title?.trim() || `Idea #${c.id}`}
                      </div>
                      {c.seed_idea?.trim() && (
                        <div className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                          {c.seed_idea.trim()}
                        </div>
                      )}
                      <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mt-1">
                        {attachingId === c.id
                          ? 'Attaching…'
                          : elsewhere
                            ? `On another arc · ${stageNote}`
                            : onThisArc
                              ? `This arc · ${stageNote}`
                              : `Unassigned · ${c.status ?? 'draft'}`}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
}
